"use client";

/**
 * PositionsTableWidget — dense positions table with side, real status, actions.
 * FinTech UX: mono nums, never hardcode every row as Open.
 */
import React, { useMemo } from "react";
import { Skeleton, Tooltip } from "antd";
import {
  RiseOutlined,
  FallOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { DeskButton, DeskChip, DeskSideChip } from "@/shared/components/desk";
import { EnhancedTable } from "@/shared/components/tables/EnhancedTable";
import { SensitiveDataMask } from "@/shared/components/security/SensitiveDataMask";
import { formatCurrency } from "@/shared/utils/formatters";
import {
  formatQty,
  formatSignedCurrency,
  positionAvgPrice,
  positionMarkPrice,
  positionMarketValue,
  positionUnrealizedPnl,
  resolvePositionSide,
  resolvePositionStatus,
  tradeDeskHref,
} from "./lib/deskFormat";
import styles from "./WidgetChrome.module.css";
import local from "./TableWidgets.module.css";

export interface PositionsTableWidgetProps {
  positions: Record<string, unknown>[] | null | undefined;
  loading?: boolean;
  /** Navigate helper (router.push) */
  onNavigate: (href: string) => void;
  emptyTradeHref?: string;
  emptyPortfolioHref?: string;
  className?: string;
  maskValues?: boolean;
  /** Compact account-book columns for the dashboard side pane. */
  compact?: boolean;
}

function statusTone(status: string): "success" | "warning" | "default" {
  const key = status.toLowerCase();
  if (key === "open" || key === "active" || key === "closed" || key === "filled") {
    return "success";
  }
  if (key === "pending" || key === "partial") return "warning";
  return "default";
}

function positionRowKey(row: Record<string, unknown>): string {
  return String(
    row.id ??
    row.symbol ??
    [row.side, row.quantity, row.average_price, row.current_price].join(":")
  );
}

function positionAgentId(row: Record<string, unknown>): string {
  for (const key of ["agent_id", "agentId"]) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function compactAgentId(value: string, compact: boolean): string {
  const maxLength = compact ? 10 : 14;
  if (value.length <= maxLength) return value;
  return compact
    ? `${value.slice(0, 4)}…${value.slice(-3)}`
    : `${value.slice(0, 7)}…${value.slice(-5)}`;
}

export function PositionsTableWidget({
  positions,
  loading = false,
  onNavigate,
  emptyTradeHref = "/trading/execution",
  emptyPortfolioHref = "/portfolio",
  className = "",
  maskValues = true,
  compact = false,
}: PositionsTableWidgetProps) {
  const list = positions ?? [];
  const ready = positions != null;
  const count = list.length;
  const hasAgentAttribution = useMemo(
    () => list.some((row) => Boolean(positionAgentId(row))),
    [list]
  );
  const compactTableWidth = hasAgentAttribution ? 434 : 442;

  const columns = useMemo(() => {
    const allColumns = [
      {
        title: "Symbol",
        dataIndex: "symbol",
        key: "symbol",
        width: 88,
        render: (s: string) => (
          <span className={styles.cellSymbol}>{s || "—"}</span>
        ),
      },
      {
        title: "Side",
        key: "side",
        width: 72,
        render: (_: unknown, row: Record<string, unknown>) => {
          const side = resolvePositionSide(row);
          return <DeskSideChip side={side} />;
        },
      },
      {
        title: "Qty",
        dataIndex: "quantity",
        key: "quantity",
        align: "right" as const,
        width: 88,
        render: (q: number) => (
          <span className={`${styles.tabular} ${styles.cellNum}`}>
            {formatQty(q)}
          </span>
        ),
      },
      {
        title: "Avg",
        key: "avg",
        align: "right" as const,
        width: 96,
        render: (_: unknown, row: Record<string, unknown>) => {
          const p = positionAvgPrice(row);
          return (
            <span className={`${styles.tabular} ${styles.cellNum}`}>
              {p != null ? formatCurrency(p) : "—"}
            </span>
          );
        },
      },
      {
        title: "Mark",
        key: "mark",
        align: "right" as const,
        width: 96,
        render: (_: unknown, row: Record<string, unknown>) => {
          const p = positionMarkPrice(row);
          return (
            <span className={`${styles.tabular} ${styles.cellNum}`}>
              {p != null ? formatCurrency(p) : "—"}
            </span>
          );
        },
      },
      {
        title: "Mkt value",
        key: "value",
        align: "right" as const,
        width: 112,
        render: (_: unknown, row: Record<string, unknown>) => {
          const v = positionMarketValue(row);
          const text = v != null ? formatCurrency(v) : "—";
          return (
            <span className={`${styles.tabular} ${styles.cellNum}`}>
              {v != null && maskValues ? (
                <SensitiveDataMask value={text} />
              ) : (
                text
              )}
            </span>
          );
        },
      },
      {
        title: "uP&L",
        key: "pnl",
        align: "right" as const,
        width: 120,
        render: (_: unknown, row: Record<string, unknown>) => {
          const pnl = positionUnrealizedPnl(row);
          if (pnl == null) {
            return <span className={styles.muted}>—</span>;
          }
          const up = pnl >= 0;
          return (
            <span
              className={`${styles.tabular} ${styles.cellPnl} ${up ? styles.deltaUp : styles.deltaDown
                }`}
            >
              {up ? (
                <RiseOutlined aria-hidden />
              ) : (
                <FallOutlined aria-hidden />
              )}
              {formatSignedCurrency(pnl)}
            </span>
          );
        },
      },
      ...(hasAgentAttribution
        ? [
          {
            title: "Agent",
            key: "agent",
            width: 112,
            render: (_: unknown, row: Record<string, unknown>) => {
              const agentId = positionAgentId(row);
              if (!agentId) return <span className={styles.muted}>—</span>;
              return (
                <Tooltip title={agentId}>
                  <code className={styles.mono} title={agentId}>
                    {compactAgentId(agentId, compact)}
                  </code>
                </Tooltip>
              );
            },
          },
        ]
        : []),
      {
        title: "State",
        key: "status",
        width: 80,
        render: (_: unknown, row: Record<string, unknown>) => {
          const st = resolvePositionStatus(row);
          return <DeskChip tone={statusTone(st)}>{st}</DeskChip>;
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 120,
        fixed: "right" as const,
        render: (_: unknown, row: Record<string, unknown>) => {
          const symbol = String(row.symbol ?? "");
          if (!symbol) return null;
          return (
            <div className={styles.actionsCell}>
              <DeskButton type="link" onClick={() => onNavigate(tradeDeskHref(symbol))}>
                Trade
              </DeskButton>
              <DeskButton
                type="link"
                onClick={() => onNavigate(tradeDeskHref(symbol, "close"))}
              >
                Close
              </DeskButton>
            </div>
          );
        },
      },
    ];

    if (!compact) return allColumns;

    const compactWidths: Record<string, number> = hasAgentAttribution
      ? {
        symbol: 56,
        side: 56,
        quantity: 52,
        pnl: 74,
        agent: 84,
        actions: 112,
      }
      : {
        symbol: 60,
        side: 60,
        quantity: 56,
        mark: 72,
        pnl: 82,
        actions: 112,
      };
    const visible = new Set(Object.keys(compactWidths));

    return allColumns
      .filter((column) => visible.has(String(column.key)))
      .map((column) => ({
        ...column,
        width: compactWidths[String(column.key)] ?? column.width,
      }));
  }, [compact, hasAgentAttribution, maskValues, onNavigate]);

  if (loading) {
    return (
      <div className={className}>
        <Skeleton active paragraph={{ rows: 5 }} />
      </div>
    );
  }

  if (!ready || count === 0) {
    return (
      <div className={`${local.empty} ${className}`.trim()}>
        <div className={local.emptyIcon} aria-hidden>
          <BarChartOutlined />
        </div>
        <p className={local.emptyTitle}>
          {!ready ? "Waiting for positions" : "No open positions"}
        </p>
        <p className={local.emptyDesc}>
          {!ready
            ? "Holdings load when the trading connection is ready."
            : "When you open a trade, holdings appear here with side, mark, and unrealized P&L."}
        </p>
        <div className={local.emptyActions}>
          <DeskButton type="primary" onClick={() => onNavigate(emptyTradeHref)}>
            Place a trade
          </DeskButton>
          <DeskButton onClick={() => onNavigate(emptyPortfolioHref)}>
            View portfolio
          </DeskButton>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.tableWrap} ${className}`.trim()}>
      <EnhancedTable
        dataSource={list}
        columns={columns}
        pagination={false}
        showToolbar={false}
        zebraStriped={false}
        density="dense"
        rowKey={(row) => positionRowKey(row as Record<string, unknown>)}
        size="small"
        scroll={{
          x: compact ? compactTableWidth : hasAgentAttribution ? 1012 : 900,
        }}
      />
    </div>
  );
}
